/**
 * The shapes the money layer works in.
 *
 * Lifted from `packages/core/payments/session.ts` when the calculator moved into
 * this package — the wire leg and the state the selectors take. Types only: the
 * reducer that used to own them is v2's and is not coming.
 */
import type { LegState } from "../engine/machine";

export const LEG_STATUS = {
  REQUESTED: "Requested",
  PENDING: "Pending",
  POSTED: "Posted",
  FAILED: "Failed",
} as const;
export type LegStatus = (typeof LEG_STATUS)[keyof typeof LEG_STATUS];

/** Wire values. `NC` is matched by rista-library's calculateForNC branch — do not rename. */
export const TXN_TYPE = {
  FULL: "fullPayment",
  SPLIT: "splitPayment",
  PARTIAL: "partialPayment",
  NC: "NC",
} as const;
export type TxnType = (typeof TXN_TYPE)[keyof typeof TXN_TYPE];

export const LEG_TYPE = { EXTERNAL: "External", COUPON: "Coupon" } as const;
/** Modes whose leg is settled asynchronously and blocks commit while REQUESTED. */
export const ASYNC_MODES = ["Rista Card", "Prepaid"] as const;

/**
 * Fields a PROVIDER owns on a leg — written by `applyProviderStatus` when a leg
 * posts, and read by the host when it builds a request body.
 *
 * They stay FLAT on the leg rather than nested under one key, because this is the
 * wire shape: the backend and every v1 terminal read them at the top level of the
 * payment object, and v1 and v2 terminals sync sales to each other over LAN.
 * Nesting them would be tidier and would break that.
 *
 * Enumerated rather than left to an index signature so `postedFields` below is
 * checked at compile time — a typo there used to be silent. Unknown fields the
 * server sends still survive at runtime (every merge here is a spread); they are
 * simply not part of the contract.
 */
export interface ProviderFields {
  transactionId?: string;
  transactionSuffix?: string;
  /** Qwikcilver settles with these two — see PROVIDERS.qwikcilver.postedFields */
  approvalCode?: string;
  batchNumber?: string;
  /** opaque provider blob, echoed back on status calls */
  data?: Record<string, any>;
  /** provider payload the PayTM intent paths read (see readIntent in ./providers) */
  extras?: Record<string, any>;
  qrData?: string;
  /** payment-def presentation carried on the leg: provider id and logo */
  pid?: string;
  img?: string;
  note?: string;
  reason?: string;
}

export interface PaymentLeg extends ProviderFields {
  paymentId: string;
  mode: string;
  type?: string;
  /** collected — for an unposted External leg this stays 0, see getNewPaymentInfo */
  amount: number;
  /** asked for — External legs only */
  requestAmount?: number;
  /** wire status the backend sees */
  paymentStatus?: LegStatus;
  /** machine state — the one the flow reasons about (see ./machine) */
  legState?: LegState;
  posted?: boolean;
  isCash?: boolean;
  reference?: string;
  referenceMandatory?: boolean;
  /** pre-rounding amount, web only — see getUpdatedPaymentAmount in ./legs */
  originalAmount?: number;
  errorMessage?: string;
  /**
   * Picked but NOT yet part of the bill — v1's `checkoutContextState.selectedPayment`
   * while it is still outside the `payments` Map (`UpdatePaymentLeg:611`). Only SPLIT
   * produces one, and only ADD PAYMENT clears it (`defaultFlow.handleSplitClick:120`).
   * `normalizeLegs` skips it, so it reaches neither the totals nor the wire.
   */
  draft?: boolean;
}

export interface CloseReason {
  reason?: string;
  remarks?: string;
}

/**
 * What the money rules need from the RECORD being paid.
 *
 * Replaces `PaymentSessionState`, which was a nine-field object the domain had
 * to CONSTRUCT — `toSessionState` — on every one of nine call sites, so that a
 * selector reading one or two fields could be handed all nine. Every field here
 * comes off the record the domain is already holding; `legs` are passed
 * separately because they are the engine's, not the record's; and the two that
 * were neither (`activeLegId`, and whether a provider is holding a leg) are
 * explicit arguments to the two functions that actually read them.
 *
 * A sale's `SaleRecord` satisfies this structurally — there is nothing to build.
 */
export interface SaleContext {
  sale: any;
  tip?: number;
  txnType?: TxnType;
  closeReason?: CloseReason | null;
  returnSale?: boolean;
}

